import {
  Card,
  Table,
  TableBody,
  Chip,
  Box,
  Link,
  IconButton,
  LinearProgress,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DataRow } from 'app/components';
import {
  SaveAlt,
  CheckCircle,
  Error,
  HourglassEmpty
} from '@material-ui/icons';
import clsx from 'clsx';
import React from 'react';
import TitleField from '../TitleField';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    width: '100%',
  },

  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },

  confidenceContainer: {
    width: '100%',
  },

  confidenceBar: {
    height: 8,
    borderRadius: 4,
  },

  section: {
    marginTop: theme.spacing(3),
  },

  mediaBox: {
    textAlign: 'center',
    marginTop: theme.spacing(1),
  },

  media: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 10,
    border: '1px solid #ddd',
  },

  actionRow: {
    display: 'flex',
    justifyContent: 'center', // 🔥 CENTERED
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },

  actionLink: {
    fontSize: 13,
    cursor: 'pointer',
  },
}));

const getStatusInfo = (status) => {
  const map = {
    LIVENESS_PASSED: {
      color: '#4caf50',
      icon: <CheckCircle />,
      label: 'Liveness Test Passed'
    },
    LIVENESS_FAILED: {
      color: '#f44336',
      icon: <Error />,
      label: 'Liveness Failed'
    },
  };

  return map[status] || {
    color: '#9e9e9e',
    icon: <HourglassEmpty />,
    label: status || '-',
  };
};

const RegtankLivenessTable = ({ liveness = {}, className }) => {
  const classes = useStyles();

  const handleDownload = async (url, filename) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    } catch (e) {
      console.error(e);
    }
  };

  const statusInfo = getStatusInfo(liveness.status);

  return (
    <Card className={clsx(classes.card, className)}>
      <TitleField>Liveness Verification</TitleField>

      {/* 🔥 STATUS + CONFIDENCE */}
      <Table>
        <TableBody>

          <DataRow
            name="Status"
            value={
              <Box className={classes.statusContainer}>
                {React.cloneElement(statusInfo.icon, {
                  style: { color: statusInfo.color }
                })}
                <Chip
                  label={statusInfo.label}
                  style={{
                    backgroundColor: statusInfo.color,
                    color: '#fff',
                  }}
                  size="small"
                />
              </Box>
            }
          />

          <DataRow
            name="Confidence"
            value={
              liveness.confidence ? (
                <Box className={classes.confidenceContainer}>
                  <Typography variant="body2">
                    {liveness.confidence.toFixed(2)}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={liveness.confidence}
                    className={classes.confidenceBar}
                  />
                </Box>
              ) : '-'
            }
          />

        </TableBody>
      </Table>

      {/* 🔥 SELFIE */}
      {liveness.selfieUrl && (
        <Box className={classes.section}>
          <Typography variant="subtitle2">Selfie</Typography>

          <Box className={classes.mediaBox}>
            <img
              src={liveness.selfieUrl}
              alt="Selfie"
              className={classes.media}
            />
          </Box>

          <Box className={classes.actionRow}>
            <Link
              href={liveness.selfieUrl}
              target="_blank"
              className={classes.actionLink}
            >
              View
            </Link>

            <IconButton
              size="small"
              onClick={() => handleDownload(liveness.selfieUrl, 'selfie.jpg')}
            >
              <SaveAlt fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* 🔥 VIDEO */}
      {(liveness.videoUrl || liveness.selfieVideoUrl) && (
        <Box className={classes.section}>
          <Typography variant="subtitle2">Video</Typography>

          <Box className={classes.mediaBox}>
            <video
              src={liveness.videoUrl || liveness.selfieVideoUrl}
              controls
              className={classes.media}
            />
          </Box>

          <Box className={classes.actionRow}>
            <Link
              href={liveness.videoUrl || liveness.selfieVideoUrl}
              target="_blank"
              className={classes.actionLink}
            >
              View
            </Link>

            <IconButton
              size="small"
              onClick={() =>
                handleDownload(
                  liveness.videoUrl || liveness.selfieVideoUrl,
                  'video.mp4'
                )
              }
            >
              <SaveAlt fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default RegtankLivenessTable;